import { AdTooSoonError, AdUnavailableError } from './ads.js';

/**
 * Adexium's widget (docs.adexium.io/publisher/integration), for the task
 * centre's Adexium slot.
 *
 * Its script defines `AdexiumWidget`. As Adexium's own examples have it, the
 * widget is made ahead of the ad — when the Tasks page shows the slot
 * (`prepareAdexium`) — and kept, one per widget id; an ad is asked for later,
 * on the player's tap. It shows an ad only when asked: `autoMode()`, which
 * would put ads over the app on its own every few minutes, is never called.
 * Asked with `requestAd('interstitial')`, it answers with an event —
 * `adReceived` with the ad, which `displayAd` puts over the app, or
 * `noAdFound` — and then says `adDisplayed` once the ad is on screen,
 * `adPlaybackCompleted` when a video ad played to its end, and `adClosed`
 * when it is gone.
 *
 * The script is on the page from the start (index.html), after the app's own
 * so it never holds the app up; when it is not there yet, it is fetched here.
 */

interface AdexiumWidget {
  on(event: string, handler: (payload?: unknown) => void): void;
  requestAd(format: string): void;
  displayAd(ad: unknown): void;
}

type AdexiumWidgetClass = new (options: { wid: string; adFormat: string }) => AdexiumWidget;

const SCRIPT_URL = 'https://cdn.tgads.space/assets/js/adexium-widget.min.js';
const LOAD_TIMEOUT_MS = 15_000;
/** A widget made just now is given this long to set itself up before the first ad is asked of it. */
const SETTLE_MS = 1_500;
/** Unanswered this long, the request is made once more: a widget still setting up can drop the first. */
const ASK_AGAIN_MS = 7_000;
/** How long Adexium may take to answer, with an ad or with none. */
const ANSWER_TIMEOUT_MS = 20_000;
/** How long an ad may stay up before its close is no longer waited for. */
const SHOW_TIMEOUT_MS = 5 * 60_000;

/**
 * The widget's class. A script's top-level `class AdexiumWidget` is a global
 * every script can name but not a property of `window`, so it is looked up both
 * ways (the Mini App's policy allows `Function`).
 */
function widgetClass(): AdexiumWidgetClass | undefined {
  const own = (window as unknown as { AdexiumWidget?: unknown }).AdexiumWidget;
  if (typeof own === 'function') return own as AdexiumWidgetClass;
  try {
    const named = new Function('return typeof AdexiumWidget === "function" ? AdexiumWidget : undefined')() as unknown;
    return typeof named === 'function' ? (named as AdexiumWidgetClass) : undefined;
  } catch {
    return undefined;
  }
}

let loading: Promise<void> | null = null;

/** Adexium's script, once per page; rejects with AdUnavailableError when it cannot be had. */
export function loadAdexium(): Promise<void> {
  if (widgetClass()) return Promise.resolve();
  if (loading) return loading;
  loading = new Promise<void>((resolve, reject) => {
    // index.html's own copy, when the page has it: waited for rather than fetched twice.
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_URL}"]`);
    const fetched = performance.getEntriesByName(SCRIPT_URL).length > 0;
    const script = existing ?? document.createElement('script');
    const done = (error?: AdUnavailableError) => {
      window.clearTimeout(timer);
      script.removeEventListener('load', onLoad);
      script.removeEventListener('error', onError);
      if (!error) return resolve();
      if (!existing) script.remove();
      loading = null;
      reject(error);
    };
    const onLoad = () => done(widgetClass() ? undefined : new AdUnavailableError('Adexium did not load.', 'notLoaded'));
    const onError = () => done(new AdUnavailableError('Adexium could not be reached.', 'unreachable'));
    const timer = window.setTimeout(() => onError(), LOAD_TIMEOUT_MS);
    script.addEventListener('load', onLoad);
    script.addEventListener('error', onError);
    if (!existing) {
      script.src = SCRIPT_URL;
      script.async = true;
      document.head.appendChild(script);
    } else if (fetched) {
      // Already run, and still no widget: it will not come.
      onLoad();
    }
  });
  return loading;
}

/** The ad being played: the widget's handlers, set once, speak to it. */
interface Play {
  received(ad: unknown): void;
  none(): void;
  displayed(): void;
  completed(): void;
  closed(): void;
}

const widgets = new Map<string, AdexiumWidget>();
let playing: Play | null = null;

function widgetFor(widgetId: string): AdexiumWidget {
  const known = widgets.get(widgetId);
  if (known) return known;
  const Widget = widgetClass();
  if (!Widget) throw new AdUnavailableError('Adexium did not load.', 'notLoaded');
  const widget = new Widget({ wid: widgetId, adFormat: 'interstitial' });
  widget.on('adReceived', (ad) => playing?.received(ad));
  widget.on('noAdFound', () => playing?.none());
  widget.on('adDisplayed', () => playing?.displayed());
  widget.on('adPlaybackCompleted', () => playing?.completed());
  widget.on('adClosed', () => playing?.closed());
  widgets.set(widgetId, widget);
  return widget;
}

/** The widget made ahead of the ad, as Adexium's examples make it at load; quiet when it cannot be. */
export async function prepareAdexium(widgetId: string): Promise<void> {
  if (!widgetId || widgets.has(widgetId)) return;
  await loadAdexium();
  widgetFor(widgetId);
}

export interface AdexiumOutcome {
  /** How long the ad was on screen, in ms: from its first frame (or from being put up) to its closing. */
  shownMs: number;
  /** Adexium said a video ad played to its end. */
  completed: boolean;
}

/**
 * Plays one Adexium interstitial and says how long it was on screen.
 *
 * Like Adsgram's interstitial, it can be closed before it ends, so closing is
 * an outcome here, not a failure — whether the time is enough is the slot's
 * rule (adFlow.ts, watchAdexiumAd). Its close is Adexium's `adClosed`, or the
 * player touching the app again: an interstitial covers the whole screen, so
 * a touch that reaches the app means it is gone. Rejects only when there was
 * no ad to show, one is already showing, or the widget failed.
 */
export async function playAdexium(widgetId: string, hooks: { onStarted?: () => void } = {}): Promise<AdexiumOutcome> {
  if (!widgetId) throw new AdUnavailableError('The ad session is missing its Adexium widget.', 'noBlock');
  await loadAdexium();
  const fresh = !widgets.has(widgetId);
  const widget = widgetFor(widgetId);
  if (playing) throw new AdTooSoonError();
  if (fresh) await new Promise((settle) => setTimeout(settle, SETTLE_MS));

  return await new Promise<AdexiumOutcome>((resolve, reject) => {
    let putUpAt: number | null = null;
    let shownAt: number | null = null;
    let completed = false;
    let showTimer: number | undefined;
    const root = document.getElementById('root');
    const onTouch = (event: Event) => {
      if (putUpAt !== null && Date.now() - putUpAt > 1_000 && root?.contains(event.target as Node)) closed();
    };
    const finish = (settle: () => void) => {
      window.clearTimeout(askAgain);
      window.clearTimeout(answerTimer);
      window.clearTimeout(showTimer);
      document.removeEventListener('pointerdown', onTouch, true);
      playing = null;
      settle();
    };
    const noAd = () => finish(() => reject(new AdUnavailableError('No Adexium ad to show right now.', 'noFill')));
    const closed = () => {
      const from = shownAt ?? putUpAt;
      finish(() => resolve({ shownMs: from === null ? 0 : Date.now() - from, completed }));
    };
    const ask = () => {
      try {
        widget.requestAd('interstitial');
      } catch {
        finish(() => reject(new AdUnavailableError('Adexium could not ask for an ad.')));
      }
    };
    const askAgain = window.setTimeout(() => {
      if (putUpAt === null) ask();
    }, ASK_AGAIN_MS);
    const answerTimer = window.setTimeout(() => {
      if (putUpAt === null) noAd();
    }, ANSWER_TIMEOUT_MS);

    playing = {
      received: (ad) => {
        // Asked twice, it may answer twice: the first ad is the one shown.
        if (putUpAt !== null) return;
        putUpAt = Date.now();
        hooks.onStarted?.();
        showTimer = window.setTimeout(closed, SHOW_TIMEOUT_MS);
        document.addEventListener('pointerdown', onTouch, true);
        try {
          widget.displayAd(ad);
        } catch {
          finish(() => reject(new AdUnavailableError('Adexium could not show the ad.')));
        }
      },
      none: () => {
        if (putUpAt === null) noAd();
      },
      displayed: () => {
        shownAt ??= Date.now();
      },
      completed: () => {
        completed = true;
      },
      closed: () => {
        // A close before anything was put up is no answer to this request.
        if (putUpAt !== null) closed();
      },
    };
    ask();
  });
}
